import { useEffect, useRef, useState } from 'react';
import { clearApolloCache } from '@/shared/api';
import { EMITTER_CONSTANTS, STRIP_DEVICE_THUMBNAIL_INFO } from '@/shared/config';
import { useEmitter, usePaginationKeyboard } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import { createOnChangePageString } from '@/shared/lib/parsePageInput';
import { selectIsLoadingStrip, selectPageIndex, useReportStore } from '@/entities/report';
import { ID_DIV_DEVICE_STRIP_MANAGEMENT } from '@/widgets/strips-management/ui/handler';
import type { DevicePaginationContainerProps } from './devicePaginationContainer';

/**
 * Runs pagination for the device strip grid, including the page input and its keyboard shortcuts. Derives the page count from the total strip count and the per-page limit, keeps the input text in sync with the store's page index, moves pages on Z and X while the tab is hovered and no strip modal is open, mirrors the next and previous emitter signals, and clamps the index when the page count shrinks.
 *
 * @param props - Pagination props with the tab store key and whether the controls are disabled.
 * @returns An object carrying the tab store key, the disabled and loading flags, the page bounds and maximum, the page input text, size and error state, and the previous, next, change, key-down, blur and reload handlers.
 */
const useDevicePaginationContainer = ({
  isDisabled,
  keyRecoil,
}: Pick<DevicePaginationContainerProps, 'isDisabled' | 'keyRecoil'>) => {
  const pageIndex = useReportStore(selectPageIndex(keyRecoil));
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Writes the strip grid page index for this tab into the report store. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - New page index, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const totalStrip = useReportStore((s) => s.totalStripDevice);

  const [isError, setIsError] = useState(false);
  const [inputSize, setInputSize] = useState(String(pageIndex.index + 1).length + 1);
  const [pageString, setPageString] = useState<any>(pageIndex.index + 1);
  const [isOpenModaltrip, setIsOpenModalStrip] = useState(false);

  const pageStringRef = useRef(pageString);
  const maxPageNumberRef = useRef<any>();

  const STEP = STRIP_DEVICE_THUMBNAIL_INFO.stripDisplayLimit;

  const maxPageNumber = totalStrip ? Math.ceil(totalStrip / STEP) : 1;
  maxPageNumberRef.current = maxPageNumber;

  const isDisabledNext = (pageIndex.index + 1) * STEP >= totalStrip;
  const isDisabledPrev = pageIndex.index === 0;

  /**
   * Moves the device strip grid back one page. Decrements the page index held in the report store.
   *
   * @returns Nothing.
   */
  const onClickPrevious = () => {
    setPageIndex({ index: pageIndex.index - 1 });
  };

  /**
   * Advances the device strip grid to the next page. Increments the page index held in the report store.
   *
   * @returns Nothing.
   */
  const onClickNext = () => {
    setPageIndex({ index: pageIndex.index + 1 });
  };

  const onChangePageString = createOnChangePageString({
    setIsError,
    setPageString,
    pageStringRef,
    setInputSize,
  });

  /**
   * Commits the typed page number when the input loses focus. Moves to the parsed page when it falls within the page count, otherwise raises the input error flag.
   *
   * @returns Nothing.
   */
  const onBlur = () => {
    if (pageString) {
      const pageNumber = Number(pageString);
      if (pageNumber && pageNumber <= maxPageNumber) {
        const newPageIndex = Number(pageString) - 1;
        setPageIndex((prev) => (newPageIndex !== prev.index ? { index: newPageIndex } : prev));
      } else {
        setIsError(true);
      }
    }
  };

  /**
   * Forces a fresh fetch of the device strips for the current page. Clears the Apollo cache, then emits the reload-event-strip signal.
   *
   * @returns A promise resolving once the cache has been cleared and the reload signal emitted.
   */
  const onClickReloadButton = async () => {
    await clearApolloCache();
    emitter.emit(EMITTER_CONSTANTS.RELOAD_EVENT_STRIP);
  };

  const { onKeyDown } = usePaginationKeyboard({
    tabZoneId: ID_DIV_DEVICE_STRIP_MANAGEMENT,
    isDisabled,
    isBlocked: isOpenModaltrip,
    isDisabledPrev,
    isDisabledNext,
    onClickPrevious,
    onClickNext,
    pageStringRef,
    maxPageNumberRef,
    setPageIndex,
    setIsError,
    deps: [pageIndex, totalStrip, isDisabled, isOpenModaltrip],
  });

  useEffect(() => {
    setPageString(pageIndex.index + 1);
    setInputSize(String(pageIndex.index + 1).length + 1);
  }, [pageIndex]);

  useEmitter(
    EMITTER_CONSTANTS.STRIP_DEVICE_MANAGEMENT_NEXT,
    () => {
      if (!isDisabledNext) {
        onClickNext();
      }
    },
    [pageIndex, totalStrip],
  );

  useEmitter(
    EMITTER_CONSTANTS.STRIP_DEVICE_MANAGEMENT_PREV,
    () => {
      if (!isDisabledPrev) {
        onClickPrevious();
      }
    },
    [pageIndex, totalStrip],
  );

  useEmitter(
    EMITTER_CONSTANTS.OPEN_MODAL_EDIT_AI_STRIP,
    (msg: any) => {
      setIsOpenModalStrip(msg.value);
    },
    [],
  );

  useEffect(() => {
    if (maxPageNumber <= pageIndex.index) {
      setPageIndex({ index: maxPageNumber - 1 });
    }
  }, [maxPageNumber, pageIndex]);

  return {
    keyRecoil,
    isDisabled,
    isLoadingStrip,
    isDisabledNext,
    isDisabledPrev,
    maxPageNumber,
    pageString,
    inputSize,
    isError,
    onClickPrevious,
    onClickNext,
    onChangePageString,
    onKeyDown,
    onBlur,
    onClickReloadButton,
  };
};

export default useDevicePaginationContainer;
